import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

function LegalLayout({ title, children }) {
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: '100vh', background: '#0A0F1E', padding: '56px 20px 48px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#C9A84C', cursor: 'pointer', padding: 0 }}>
          <ChevronLeft size={22} />
        </button>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: '#F5F0E8', fontWeight: 700 }}>{title}</h1>
      </div>
      <div style={{ maxWidth: 640, fontSize: 13, color: '#8A8580', lineHeight: 1.8 }}>
        {children}
      </div>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy">
      <p style={{ color: '#C9A84C', marginBottom: 16 }}>Last updated: June 2026</p>
      <Section title="1. About This Policy">
        DailyRaashi (&ldquo;we&rdquo;, &ldquo;our&rdquo;, &ldquo;us&rdquo;) provides personalised Vedic astrology guidance. This Privacy Policy explains how we collect, use and protect your information when you use our app at dailyraashi.com.
      </Section>
      <Section title="2. Information We Collect">
        <ul>
          <li><strong>Account information</strong>: Name, email address or phone number provided at registration.</li>
          <li><strong>Birth details</strong>: Date, time and place of birth. This data is used solely to calculate your Vedic birth chart and generate personalised readings.</li>
          <li><strong>Current location</strong>: If you enable Muhurtas, we use your current city/coordinates to calculate auspicious timings. We do not store precise GPS location.</li>
          <li><strong>Usage data</strong>: Pages visited, features used and interactions, collected anonymously to improve the product.</li>
          <li><strong>Payment data</strong>: Payment transactions are processed by Razorpay. We store only plan type, amount and transaction status. We do not store card numbers.</li>
        </ul>
      </Section>
      <Section title="3. How We Use Your Information">
        <ul>
          <li>To generate your personalised Vedic birth chart.</li>
          <li>To produce your daily reading using AI (Anthropic Claude), using your chart data only.</li>
          <li>To send Cosmic Alarm notifications at your chosen time.</li>
          <li>To manage your subscription and payment history.</li>
          <li>To communicate account and service updates.</li>
        </ul>
      </Section>
      <Section title="4. Service Providers">
        We use the following third-party services:
        <ul>
          <li><strong>Firebase (Google)</strong>: Authentication and secure user management.</li>
          <li><strong>Anthropic Claude</strong>: AI-generated reading content. Only your chart data and language preference are shared. No personally identifiable information is sent to Anthropic.</li>
          <li><strong>Razorpay</strong>: Payment processing. Subject to Razorpay's own Privacy Policy.</li>
          <li><strong>OpenStreetMap Nominatim</strong>: City search and geocoding. Queries are not linked to your account.</li>
          <li><strong>MongoDB Atlas</strong>: Secure cloud database for storing your profile, chart and readings.</li>
        </ul>
      </Section>
      <Section title="5. We Do Not Sell Your Information">
        We do not sell, rent or trade your personal information to any third party for marketing purposes.
      </Section>
      <Section title="6. Data Retention and Deletion">
        Your data is retained as long as your account is active. You may request full account deletion from Settings &rarr; Delete Account. On deletion, your profile, birth chart, reading history and subscription records are permanently removed.
      </Section>
      <Section title="7. Contact">
        For privacy queries, email: <strong style={{ color: '#C9A84C' }}>support@dailyraashi.com</strong>
      </Section>
    </LegalLayout>
  );
}

export function TermsPage() {
  return (
    <LegalLayout title="Terms of Service">
      <p style={{ color: '#C9A84C', marginBottom: 16 }}>Last updated: June 2026</p>
      <Section title="1. Acceptance">
        By using DailyRaashi, you agree to these Terms. If you do not agree, please do not use the service.
      </Section>
      <Section title="2. The Service">
        DailyRaashi provides personalised Vedic astrology readings for guidance and entertainment purposes. <strong style={{ color: '#F5F0E8' }}>Readings are not a substitute for professional medical, legal, financial or psychological advice.</strong>
      </Section>
      <Section title="3. Account">
        You must provide accurate information at registration. You are responsible for maintaining the security of your account.
      </Section>
      <Section title="4. Subscriptions and Payments">
        <ul>
          <li>A free {'{trialDays}'}-day trial is provided with no payment required.</li>
          <li>Subscriptions are billed via Razorpay. Prices are shown at checkout.</li>
          <li>Subscription fees are non-refundable except where required by applicable law.</li>
          <li>Subscriptions auto-expire at the end of the paid period unless renewed.</li>
        </ul>
      </Section>
      <Section title="5. Prohibited Use">
        You may not use DailyRaashi to harass others, distribute misleading information or violate applicable laws.
      </Section>
      <Section title="6. Limitation of Liability">
        DailyRaashi is provided &ldquo;as is&rdquo;. We are not liable for decisions made based on astrological readings.
      </Section>
      <Section title="7. Contact">
        Email: <strong style={{ color: '#C9A84C' }}>support@dailyraashi.com</strong>
      </Section>
    </LegalLayout>
  );
}

export function DisclaimerPage() {
  return (
    <LegalLayout title="Disclaimer">
      <Section title="Guidance and Entertainment Only">
        DailyRaashi provides Vedic astrology readings for personal guidance and entertainment purposes only. All readings, recommendations and interpretations are based on traditional Jyotish principles and are not scientifically verified predictions.
      </Section>
      <Section title="Not Professional Advice">
        Nothing on DailyRaashi constitutes medical, psychological, legal, financial or professional advice. Always consult a qualified professional for such matters.
      </Section>
      <Section title="Accuracy">
        Astrological interpretations are inherently subjective. Birth chart accuracy depends on the accuracy of birth date, time and place entered by the user. We do not guarantee the accuracy of any reading or prediction.
      </Section>
      <Section title="About the AI">
        Daily readings are generated using an AI system trained to interpret Vedic astrological principles. The AI uses your birth chart data to personalise content. Results may vary.
      </Section>
    </LegalLayout>
  );
}

export function AboutJyotiPage() {
  return (
    <LegalLayout title="About Jyoti">
      <div style={{
        padding: '20px', background: 'rgba(201,168,76,0.06)',
        border: '1px solid rgba(201,168,76,0.15)', borderRadius: 16, marginBottom: 20, textAlign: 'center',
      }}>
        <p style={{ fontSize: 32, marginBottom: 8 }}>✦</p>
        <p style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: '#F5F0E8' }}>Meet Jyoti</p>
        <p style={{ fontSize: 13, color: '#C9A84C', marginTop: 4 }}>Your Personal Vedic Guide</p>
      </div>
      <Section title="Who Is Jyoti?">
        Jyoti is DailyRaashi&rsquo;s personalised astrology guide — a warm, knowledgeable companion who interprets your unique Vedic birth chart each day. Jyoti speaks to you directly, drawing on the principles of Jyotish (Vedic astrology) to offer guidance that is tailored to your chart, not a generic Sun-sign horoscope.
      </Section>
      <Section title="How Does Jyoti Work?">
        Jyoti uses your birth chart &mdash; calculated from your date, time and place of birth using Swiss Ephemeris (Lahiri Ayanamsa) &mdash; combined with the current planetary positions, your active Vimshottari Dasha period and today&rsquo;s Moon transit to create your reading.
      </Section>
      <Section title="What Is the AI Behind Jyoti?">
        Jyoti&rsquo;s voice and interpretations are generated by a leading AI model trained in Vedic astrology principles. Your birth data is used only to personalise your reading. Jyoti&rsquo;s responses are not human-generated, and readings are for guidance and entertainment only.
      </Section>
      <Section title="What Jyoti Is Not">
        Jyoti is not a licensed astrologer, therapist, doctor or financial adviser. Please treat Jyoti&rsquo;s guidance as a thoughtful perspective, not a professional recommendation.
      </Section>
    </LegalLayout>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h2 style={{ fontSize: 14, fontWeight: 700, color: '#F5F0E8', marginBottom: 8 }}>{title}</h2>
      <div style={{ fontSize: 13, color: '#8A8580', lineHeight: 1.8 }}>{children}</div>
    </div>
  );
}
